import React, { useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'react-hot-toast';

/* ------------------------------------------------------------------ */
/*  Dijital Pazarlama Eğitimi — 101 → 102 → 103 + Platform sekmeleri   */
/*  Statik eğitim sayfası (Ecem için). /egitim/dijital-pazarlama-101   */
/* ------------------------------------------------------------------ */

const EDU_TABS = [
  { id: '101', label: '101 · Temeller' },
  { id: '102', label: '102 · Orta Seviye' },
  { id: '103', label: '103 · İleri & Glowytics' },
];

const PLATFORM_TABS = [
  { id: 'google', label: 'Google Ads' },
  { id: 'meta', label: 'Meta (FB/IG)' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'programmatic', label: 'Programatik (RTB/Criteo)' },
];

/* ------------------------------ UI atoms ------------------------------ */
function Section({ n, title, children }) {
  return (
    <section className="mb-9 scroll-mt-28">
      <div className="flex items-start gap-3 mb-3">
        {n ? (
          <span className="flex-shrink-0 inline-flex items-center justify-center h-7 min-w-[28px] px-2 rounded-lg bg-indigo-50 text-indigo-600 text-[13px] font-bold font-mono ring-1 ring-indigo-100">
            {n}
          </span>
        ) : (
          <span className="flex-shrink-0 mt-1 h-5 w-1.5 rounded-full bg-indigo-500" />
        )}
        <h3 className="text-[17px] font-bold text-slate-900 leading-7 tracking-tight">{title}</h3>
      </div>
      <div className="text-[14px] text-slate-600 leading-7 space-y-3 sm:pl-[40px]">{children}</div>
    </section>
  );
}

const NOTE_TONES = {
  amber: ['bg-amber-50', 'border-amber-400', 'text-amber-900', '💡'],
  blue: ['bg-sky-50', 'border-sky-400', 'text-sky-900', 'ℹ️'],
  rose: ['bg-rose-50', 'border-rose-400', 'text-rose-900', '⚠️'],
  emerald: ['bg-emerald-50', 'border-emerald-400', 'text-emerald-900', '✅'],
};
function Note({ children, tone = 'amber' }) {
  const [bg, bd, tx, icon] = NOTE_TONES[tone];
  return (
    <div className={`flex gap-3 rounded-xl border-l-4 ${bd} ${bg} ${tx} px-4 py-3 text-[13px] leading-relaxed my-4 shadow-sm`}>
      <span className="text-base leading-none mt-0.5 select-none">{icon}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Code({ children }) {
  return (
    <div className="my-4 rounded-xl overflow-hidden ring-1 ring-slate-700/30 bg-slate-900 shadow-sm">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">şema</span>
      </div>
      <pre className="text-slate-100 text-[12px] leading-relaxed p-4 overflow-x-auto whitespace-pre">{children}</pre>
    </div>
  );
}

/* shared table shell */
function TableShell({ headers, children }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl ring-1 ring-slate-200 shadow-sm">
      <table className="w-full text-[13px] border-collapse bg-white">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
            {headers.map((h) => (
              <th key={h} className="py-2.5 px-4 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
const ROW = (i) => `border-t border-slate-100 align-top hover:bg-indigo-50/30 transition-colors ${i % 2 ? 'bg-slate-50/40' : ''}`;

function MetricTable({ title, rows }) {
  return (
    <div className="my-4">
      {title && <p className="text-[13px] font-bold text-slate-700 mb-1.5">{title}</p>}
      <TableShell headers={['Metrik', 'Ne demek', 'Formül']}>
        {rows.map((r, i) => (
          <tr key={r[0]} className={ROW(i)}>
            <td className="py-2.5 px-4 font-mono font-semibold text-slate-800 whitespace-nowrap">{r[0]}</td>
            <td className="py-2.5 px-4 text-slate-600">{r[1]}</td>
            <td className="py-2.5 px-4 text-slate-500 font-mono text-[12px]">{r[2]}</td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
}

/* huni aşaması rengi */
const STAGE_CHIP = (stage) => {
  const s = String(stage);
  const map = [
    ['Tüm huni', 'bg-indigo-100 text-indigo-700'],
    ['Farkındalık', 'bg-sky-100 text-sky-700'],
    ['Değerlendirme', 'bg-amber-100 text-amber-700'],
    ['İlgi', 'bg-violet-100 text-violet-700'],
    ['Dönüşüm', 'bg-emerald-100 text-emerald-700'],
  ];
  const hit = map.find(([k]) => s.includes(k));
  return hit ? hit[1] : 'bg-slate-100 text-slate-600';
};

/* Generic 4-col table for platform ad types */
function AdTypeTable({ rows }) {
  return (
    <TableShell headers={['Reklam / Kampanya Türü', 'Huni Aşaması', 'Ne işe yarar', 'Ne zaman kurulur']}>
      {rows.map((r, i) => (
        <tr key={r[0]} className={ROW(i)}>
          <td className="py-2.5 px-4 font-semibold text-slate-800">{r[0]}</td>
          <td className="py-2.5 px-4 whitespace-nowrap">
            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STAGE_CHIP(r[1])}`}>{r[1]}</span>
          </td>
          <td className="py-2.5 px-4 text-slate-600">{r[2]}</td>
          <td className="py-2.5 px-4 text-slate-600">{r[3]}</td>
        </tr>
      ))}
    </TableShell>
  );
}

/* Generic 3-col info table (e.g. hedefleme türleri) */
function InfoTable({ headers, rows }) {
  return (
    <TableShell headers={headers}>
      {rows.map((r, i) => (
        <tr key={r[0]} className={ROW(i)}>
          <td className="py-2.5 px-4 font-semibold text-slate-800 whitespace-nowrap">{r[0]}</td>
          <td className="py-2.5 px-4 text-slate-600">{r[1]}</td>
          <td className="py-2.5 px-4 text-slate-500 italic">{r[2]}</td>
        </tr>
      ))}
    </TableShell>
  );
}

/* Concrete campaign example cards */
function ExampleCards({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 my-3">
      {items.map((ex) => (
        <div key={ex.title} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-2.5 border-b border-slate-100">
            <p className="text-[13px] font-bold text-slate-800">{ex.title}</p>
          </div>
          <ul className="p-4 space-y-1.5">
            {ex.lines.map((l, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-indigo-500 font-semibold whitespace-nowrap min-w-[56px]">{l[0]}</span>
                <span>{l[1]}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ DATA ------------------------------ */

const METRICS_REACH = [
  ['Impression', 'Gösterim — reklam kaç kez ekranda göründü', '—'],
  ['Reach', 'Erişim — kaç FARKLI kişiye ulaşıldı', '—'],
  ['Frequency', 'Frekans — bir kişi reklamı ortalama kaç kez gördü', 'Gösterim ÷ Erişim'],
  ['Click', 'Tıklama — reklama kaç kez tıklandı', '—'],
  ['CTR', 'Tıklama oranı (ilgi çekti mi?)', 'Tıklama ÷ Gösterim'],
  ['Quality Score', 'Kalite Puanı (Google) — reklam+kelime+landing uyumu', '1–10 (Google verir)'],
  ['Ad Rank', 'Reklamın arama sonucundaki sıralama gücü', 'Teklif × Kalite Puanı'],
  ['Impression Share', 'Gösterim payı — alabileceğimin yüzde kaçını aldım', 'Alınan ÷ Uygun gösterim'],
];

const METRICS_COST = [
  ['Spend', 'Harcama — toplam reklam gideri', '—'],
  ['CPC', 'Tıklama başına maliyet', 'Harcama ÷ Tıklama'],
  ['CPM', '1000 gösterim maliyeti', '(Harcama ÷ Gösterim) × 1000'],
  ['CPV', 'İzlenme başına maliyet (video)', 'Harcama ÷ İzlenme'],
  ['CPA / CPL', '1 dönüşüm / müşteri adayı maliyeti', 'Harcama ÷ Dönüşüm'],
  ['CAC', 'Yeni müşteri kazanma maliyeti', 'Pazarlama gideri ÷ Yeni müşteri'],
];

const METRICS_CONV = [
  ['Conversion', 'Dönüşüm — istediğimiz aksiyon (satış, form, arama)', '—'],
  ['Conversion Value', 'Dönüşüm değeri — dönüşümlerin getirdiği ciro', 'Σ sipariş tutarı'],
  ['CVR', 'Dönüşüm oranı', 'Dönüşüm ÷ Tıklama'],
  ['Revenue', 'Gelir / Ciro — toplam satış tutarı', '—'],
  ['ROAS', 'Reklam getirisi (en kritik metrik)', 'Dönüşüm Değeri ÷ Harcama'],
  ['ROI', 'Yatırım getirisi (kârı dikkate alır)', '(Kâr − Maliyet) ÷ Maliyet'],
  ['AOV', 'Ortalama sepet tutarı', 'Gelir ÷ Sipariş'],
  ['LTV', 'Müşteri yaşam boyu değeri', 'Ort. sipariş × tekrar × ömür'],
  ['MER', 'Toplam pazarlama verimliliği (blended)', 'Toplam ciro ÷ Toplam reklam harcaması'],
  ['Break-even ROAS', 'Başa-baş ROAS — altı zarar, üstü kâr', '1 ÷ Kâr Marjı'],
];

const METRICS_GA4 = [
  ['Session', 'Oturum — bir ziyaretin tamamı', '—'],
  ['Engaged Session', 'Etkileşimli oturum (>10sn / 2+ sayfa / dönüşüm)', '—'],
  ['Engagement Rate', 'Etkileşim oranı (bounce yerine GA4 kullanır)', 'Etkileşimli ÷ Toplam oturum'],
  ['Bounce Rate', 'Hemen çıkma — etkileşimsiz oturum oranı', '1 − Etkileşim oranı'],
  ['view_item', 'Ürün detay görüntüleme (product detail view)', '—'],
  ['add_to_cart', 'Sepete ekleme', '—'],
  ['begin_checkout', 'Ödeme adımına geçme', '—'],
  ['add_shipping/payment_info', 'Kargo / ödeme bilgisi girme', '—'],
  ['purchase', 'Satın alma (e-ticaret ana olayı)', '—'],
  ['Cart-to-Detail', 'Detayı görenin sepete ekleme oranı', 'add_to_cart ÷ view_item'],
  ['Purchase-to-Detail', 'Detayı görenin satın alma oranı', 'purchase ÷ view_item'],
  ['Checkout Completion', 'Ödemeye başlayanın tamamlama oranı', 'purchase ÷ begin_checkout'],
];

const ATTRIBUTION = [
  ['Last-click', 'Tüm krediyi SON tıklamaya verir', 'Basit ama üst-huniyi (Meta/YouTube) yok sayar; yaygın ama yanıltıcı'],
  ['First-click', 'Tüm krediyi İLK temasa verir', 'Talep yaratan kanalı abartır, kapatan kanalı görmez'],
  ['Linear', 'Krediyi tüm temas noktalarına eşit dağıtır', 'Adil ama gerçek katkıyı yansıtmaz'],
  ['Time-decay', 'Satışa yakın temaslar daha çok kredi alır', 'Uzun satış döngülerinde mantıklı'],
  ['Position-based (U)', 'İlk + son temasa %40, ortaya %20', 'Hem keşfi hem kapatmayı ödüllendirir'],
  ['Data-driven (DDA)', 'Algoritma gerçek veriden katkıyı dağıtır', 'EN DOĞRUSU — bizim kullandığımız model'],
];

const PLATFORMS = {
  google: {
    name: 'Google Ads',
    intro:
      'Google = talebi yakalama. İnsan zaten bir şey arıyor; biz o anda karşısına çıkıyoruz. Ek olarak Google’ın görsel/video ağında (YouTube, Display, Discover) talep de yaratabiliriz.',
    note: ['blue', 'E-ticarette ana satış motoru genelde Performance Max + Shopping’dir. Arama ise “net talep” için (marka, jenerik, rakip kelimeler).'],
    types: [
      ['Arama (Search)', 'Dönüşüm', 'Anahtar kelime arayan kişiye metin reklam', 'Net talep varken: marka koruma, jenerik & rakip kelimeler'],
      ['Performance Max', 'Tüm huni', 'Tek kampanyada tüm Google envanteri (Arama, Shopping, Display, YouTube, Gmail, Maps) otomatik optimize', 'E-ticaret ana satış kampanyası; feed + dönüşüm sinyali hazırsa'],
      ['Shopping (Standart)', 'Dönüşüm', 'Görsel + fiyatlı ürün reklamı (Merchant Center feed)', 'Ürün kontrolü istenince, PMax dışında ek katman'],
      ['Display (GDN)', 'Farkındalık / Retargeting', 'Sitelerde banner; ucuz geniş erişim', 'Bilinirlik + siteyi gezeni geri çağırma'],
      ['YouTube (Video)', 'Farkındalık / İlgi', 'Video reklam (skippable, bumper, in-feed)', 'Marka tanıtımı, lansman, geniş kitle'],
      ['Demand Gen', 'İlgi', 'Discover/Gmail/YouTube feed — sosyal benzeri görsel keşif', 'Meta tarzı talep yaratmayı Google tarafında yapma'],
      ['App', 'Dönüşüm (install)', 'Uygulama indirme/aksiyon kampanyası', 'Mobil uygulama büyütme'],
    ],
    targetingTitle: 'Hedefleme türleri & örnekler',
    targeting: [
      ['Anahtar kelime — Broad', 'Geniş eşleme; ilgili tüm aramalar', '“koşu ayakkabısı” → “yastıklı koşu ayakkabısı modelleri”'],
      ['Anahtar kelime — Phrase', 'Sıra korunarak içeren aramalar', '“kadın koşu ayakkabısı” → “ucuz kadın koşu ayakkabısı”'],
      ['Anahtar kelime — Exact', 'Tam/çok yakın eşleme', '[nike pegasus 41] → sadece bu niyet'],
      ['Negatif kelime', 'İstenmeyen aramayı dışla', '“ücretsiz”, “ikinci el”, “tamir”'],
      ['In-market', 'Şu an satın alma niyeti olanlar', '“Spor ayakkabı” pazarında aktif arayanlar'],
      ['Affinity', 'Uzun vadeli ilgi alanı', '“Koşu & fitness meraklıları”'],
      ['Custom segment', 'Kelime/URL/uygulama ile özel kitle', 'Rakip site URL’lerini gezenler'],
      ['Remarketing (RLSA)', 'Siteyi gezeni aramada tekrar yakala', 'Sepete atıp 7 gün almayanlar'],
      ['Customer Match', 'Kendi e-posta/telefon listeni yükle', 'Mevcut müşteriler → upsell / hariç tutma'],
      ['Demografi & Geo', 'Yaş, cinsiyet, gelir, konum, cihaz', '25-44, İstanbul + 20km, yalnız mobil'],
    ],
    examples: [
      { title: 'Marka koruma (Search)', lines: [['Hedef', 'Marka kelimelerinde 1. sırada kalmak'], ['Kelime', '[marka adı] Exact + Phrase'], ['Teklif', 'Max Conversions, küçük bütçe, çok yüksek ROAS']] },
      { title: 'E-ticaret ana satış (PMax)', lines: [['Feed', 'Merchant Center + dönüşüm sinyali'], ['Sinyal', 'Müşteri listesi + in-market kitlesi'], ['Teklif', 'Target ROAS']] },
      { title: 'Sepet bırakma (Display retargeting)', lines: [['Kitle', 'Remarketing: sepete atıp 7g almayan'], ['Format', 'Dinamik responsive banner'], ['Mesaj', '“Sepetin seni bekliyor” + indirim']] },
      { title: 'Rakip yakalama (Search)', lines: [['Kelime', 'Rakip marka adları (Broad/Phrase)'], ['Negatif', 'Kendi markamız hariç'], ['Mesaj', 'Fark yaratan üstünlük + teklif']] },
    ],
  },
  meta: {
    name: 'Meta (Facebook & Instagram)',
    intro:
      'Meta = talep yaratma. İnsan ürünü aramıyor; ilgi ve davranışına göre önüne çıkıyoruz. Kampanya “hedef”e (objective) göre kurulur; format (Reels, Stories, Carousel, Collection) ayrıca seçilir.',
    note: ['blue', 'E-ticaret için en güçlü ikili: Advantage+ Shopping (yeni müşteri) + Catalog/DPA (gezilen ürünü geri gösterme).'],
    types: [
      ['Awareness (Bilinirlik)', 'Farkındalık', 'En düşük maliyetle erişim / akılda kalıcılık', 'Yeni marka, lansman, geniş kitle'],
      ['Traffic (Trafik)', 'İlgi', 'Siteye/uygulamaya tıklama getirir', 'İçerik, blog, ucuz ziyaretçi'],
      ['Engagement', 'İlgi', 'Etkileşim, mesaj, video izleme', 'Topluluk büyütme, DM, video'],
      ['Leads', 'Değerlendirme', 'Form ile müşteri adayı toplar', 'Hizmet / B2B / randevu'],
      ['Sales (Conversions)', 'Dönüşüm', 'Pixel ile satış optimizasyonu', 'E-ticaret ana satış kampanyası'],
      ['Advantage+ Shopping (ASC)', 'Dönüşüm', 'Otomatik e-ticaret satış kampanyası (PMax muadili)', 'Katalog + pixel hazırsa en güçlü satış aracı'],
      ['Catalog / DPA (Dinamik)', 'Dönüşüm / Retargeting', 'Kişinin gezdiği ürünü otomatik gösterir', 'Sepette bırakanı geri çağırma + yeni kitleye ürün'],
    ],
    targetingTitle: 'Hedefleme türleri & örnekler',
    targeting: [
      ['Core — Demografi', 'Yaş, cinsiyet, dil, eğitim, ilişki', '25-45, kadın, Türkçe'],
      ['Core — Konum', 'Şehir, yarıçap, “burada yaşayan/gezen”', 'İstanbul + 25km, son ziyaretçiler'],
      ['Core — İlgi (Interests)', 'Beğeni/ilgi alanı', '“Koşu”, “Yoga”, “Lüks moda”, rakip markalar'],
      ['Core — Davranış (Behaviors)', 'Eylem bazlı segment', '“Online alışveriş yapanlar”, “yeni cihaz alanlar”'],
      ['Custom — Web (Pixel)', 'Sitende olay yapanlar', 'view_content / add_to_cart son 14 gün'],
      ['Custom — Müşteri listesi', 'CRM e-posta/telefon yükleme', 'Mevcut müşteriler (hariç tut veya upsell)'],
      ['Custom — Etkileşim', 'IG/FB etkileşim, video izleyen, form açan', 'Videonun %75’ini izleyenler'],
      ['Lookalike (LAL)', 'Kaynağa benzer yeni kitle (%1–10)', 'Son 180g satın alanların %1 LAL’ı'],
      ['Advantage+ Audience', 'Hedeflemeyi Meta makinesine bırak', 'Geniş + sistem optimize eder (ASC)'],
    ],
    examples: [
      { title: 'Yeni müşteri — ASC', lines: [['Hedef', 'Sales (Advantage+ Shopping)'], ['Kitle', 'Advantage+ (geniş) / mevcut alıcıları hariç'], ['Kreatif', 'Çok sayıda varyant + katalog']] },
      { title: 'Sepet retargeting — DPA', lines: [['Kitle', 'add_to_cart son 14g, purchase HARİÇ'], ['Format', 'Dinamik katalog (gezdiği ürün)'], ['Mesaj', 'Aciliyet + indirim kodu']] },
      { title: 'Lookalike prospecting', lines: [['Hedef', 'Sales'], ['Kitle', '%1 LAL (son 180g alıcılar)'], ['Kreatif', 'En iyi performans gösterenler']] },
      { title: 'Lansman farkındalık', lines: [['Hedef', 'Awareness / Video Views'], ['Kitle', 'Geniş + temel ilgi alanları'], ['Format', 'Reels + Stories video']] },
    ],
  },
  tiktok: {
    name: 'TikTok Ads',
    intro:
      'TikTok = genç kitle + video-first farkındalık. Eğlence akışının içinde “reklam gibi durmayan”, yerel (native) içerik kazanır. Satış da var ama güçlü tarafı üst huni.',
    note: ['amber', 'Spark Ads bir hedef değil formattır: mevcut organik bir postu reklama dönüştürür — daha yüksek güven ve etkileşim verir.'],
    types: [
      ['Reach', 'Farkındalık', 'En geniş kitleye ulaşma', 'Lansman, marka bilinirliği'],
      ['Video Views', 'Farkındalık', 'Ucuz izlenme', 'İçerik yayma, hatırlatma'],
      ['Traffic', 'İlgi', 'Siteye tıklama', 'Kampanya/landing trafiği'],
      ['Community Interaction', 'İlgi', 'Takip & etkileşim büyütme', 'Hesap büyütme'],
      ['Product Sales (Catalog/Shop)', 'Dönüşüm', 'Katalog ile ürün satışı', 'E-ticaret feed + pixel hazırsa'],
      ['Lead Generation', 'Değerlendirme', 'Form ile müşteri adayı', 'Hizmet / kayıt'],
      ['App Promotion', 'Dönüşüm (install)', 'Uygulama indirme', 'Mobil uygulama büyütme'],
    ],
    targetingTitle: 'Hedefleme türleri & örnekler',
    targeting: [
      ['Demografi & Konum', 'Yaş, cinsiyet, dil, bölge', '18-34, Türkiye'],
      ['Interests', 'İlgi kategorisi', '“Güzellik & bakım”, “Spor”, “Oyun”'],
      ['Behaviors', 'Video etkileşimi / creator etkileşimi', 'Bir hashtag’le veya creator’la etkileşenler'],
      ['Custom Audience', 'Müşteri listesi / web / app / lead', 'Siteyi gezen ama almayan'],
      ['Lookalike', 'Kaynağa benzer yeni kitle', 'Alıcıların benzerleri'],
      ['Automatic / Smart', 'Hedeflemeyi sisteme bırak', 'Yeni hesapta hızlı öğrenme'],
    ],
    examples: [
      { title: 'Lansman farkındalık', lines: [['Hedef', 'Reach / Video Views'], ['Kitle', 'Geniş demografi + 1-2 interest'], ['Format', 'Spark Ads (creator videosu)']] },
      { title: 'Katalog satış', lines: [['Hedef', 'Product Sales'], ['Kitle', 'Web visitors + Lookalike'], ['Format', 'Dinamik ürün videosu']] },
    ],
  },
  pinterest: {
    name: 'Pinterest Ads',
    intro:
      'Pinterest = görsel keşif & ilham. Kullanıcı genelde bir plan yapıyor (ev, düğün, moda, hediye) → satın alma niyeti yüksek. Reklam formatları “pin” olarak yayılır. Önemli: Pinterest aynı zamanda bir arama motoru — keyword hedefleme güçlüdür.',
    note: ['emerald', 'Pin formatları: Standart, Video, Carousel, Collections, Shopping (feed). Hedef seçimi kampanya kurulurken yapılır.'],
    types: [
      ['Awareness', 'Farkındalık', 'Geniş görünürlük', 'Marka / koleksiyon tanıtımı'],
      ['Consideration (Traffic)', 'İlgi', 'Pin’den siteye tıklama', 'Trafik, içerik keşfi'],
      ['Conversions', 'Dönüşüm', 'Pixel ile satış/aksiyon optimizasyonu', 'Net dönüşüm hedefi'],
      ['Catalog Sales (Shopping)', 'Dönüşüm', 'Ürün feed ile alışveriş reklamı', 'E-ticaret katalog satışı'],
    ],
    targetingTitle: 'Hedefleme türleri & örnekler',
    targeting: [
      ['Interests', 'İlgi kategorisi', '“Ev dekorasyonu”, “Düğün”, “Hediye fikirleri”'],
      ['Keywords', 'Arama bazlı (Pinterest araması)', '“yılbaşı hediyesi”, “salon dekoru”'],
      ['Audiences — Visitors', 'Pixel/tag ile siteyi gezenler', 'Ürün sayfası ziyaretçileri'],
      ['Audiences — Customer list', 'Kendi listeni yükle', 'Mevcut müşteriler'],
      ['Actalike', 'Lookalike muadili', 'En iyi kitleye benzeyenler'],
      ['Demografi & Konum', 'Yaş, cinsiyet, dil, bölge', '25-44, kadın, TR'],
    ],
    examples: [
      { title: 'Sezonluk satış', lines: [['Hedef', 'Conversions / Catalog'], ['Kitle', 'Keyword “yılbaşı hediye” + ilgi'], ['Format', 'Shopping / Collections pin']] },
      { title: 'İlham → trafik', lines: [['Hedef', 'Consideration'], ['Kitle', 'İlgi: ev dekorasyonu'], ['Format', 'Video / Carousel pin']] },
    ],
  },
  programmatic: {
    name: 'Programatik (RTB House / Criteo)',
    intro:
      'Programatik = gerçek zamanlı açık artırmayla, üçüncü parti sitelerde otomatik banner gösterimi. En güçlü yanı dinamik retargeting: kişinin gezdiği ürünü web genelinde tekrar önüne getirmek.',
    note: ['rose', 'Çoğunlukla ALT-HUNİ çalışır. Gerçek katkıyı abartmamak için bağımsız attribution şart — yoksa zaten alacak kişiye kredi yazıp ROAS’ı şişirir.'],
    types: [
      ['Dinamik Retargeting', 'Dönüşüm', 'Gezilen/sepete atılan ürünü web genelinde tekrar gösterir', 'Site trafiği + ürün feed varken'],
      ['Prospecting', 'Farkındalık / İlgi', 'Benzer kitleye yeni ürün tanıtımı', 'Yeni müşteri arama (retargeting doyunca)'],
    ],
    targetingTitle: 'Hedefleme türleri & örnekler',
    targeting: [
      ['1st-party — Ürün görüntüleyen', 'Sitende belirli ürünü gezen', 'X ürününe bakıp almayan (dinamik geri gösterim)'],
      ['1st-party — Sepet bırakan', 'Sepete atıp tamamlamayan', 'Son 7 gün checkout başlatmayan'],
      ['Commerce Audience', 'Criteo/RTB alışveriş davranış grafiği', 'Kategoriyle ilgilenen ama henüz almayan'],
      ['Lookalike / Prospecting', 'Mevcut alıcıya benzer yeni kitle', 'En iyi müşterilere benzeyenler'],
      ['Contextual', 'İçerik/sayfa bağlamına göre', 'İlgili kategori içerikli sitelerde'],
      ['Frequency cap', 'Aynı kişiye gösterim sınırı', 'Günde max 3 — bunaltmamak için'],
    ],
    examples: [
      { title: 'Dinamik retargeting', lines: [['Kitle', 'Ürün görüntüleyen / sepet bırakan'], ['Format', 'Dinamik banner (gezdiği ürün)'], ['Kontrol', 'Frequency cap + bağımsız ölçüm']] },
      { title: 'Prospecting', lines: [['Kitle', 'Lookalike / commerce audience'], ['Format', 'Kategori bazlı banner'], ['Amaç', 'Yeni trafik (retargeting doyunca)']] },
    ],
  },
};

/* ------------------------------ QUIZ (Excel) ------------------------------ */
const S = (a) => ['S', a]; // solve cell (boş bırakılır, cevap anahtarında dolu)
const NA = '—';
const QCOLS = [
  'No', 'Senaryo', 'Harcama (₺)', 'Gösterim', 'Erişim', 'Frekans', 'Tıklama', 'CTR (%)', 'CPC (₺)', 'CPM (₺)',
  'Dönüşüm', 'Dön. Oranı (%)', 'CPA (₺)', 'Dön. Değeri (₺)', 'ROAS', 'AOV (₺)', 'Kâr Marjı (%)', 'Başa-baş ROAS', 'Kâr/Zarar',
];
const QROWS = [
  [1, 'Isınma turu', 5000, 200000, NA, NA, 4000, S(2), S(1.25), S(25), 80, S(2), S(62.5), 20000, S(4), S(250), NA, NA, NA],
  [2, 'Gösterimi geri bul', 3000, S(50000), NA, NA, 1500, 3, S(2), S(60), 30, S(2), S(100), 9000, S(3), S(300), NA, NA, NA],
  [3, "ROAS'tan değeri çöz", 8000, 400000, NA, NA, 2000, S(0.5), S(4), S(20), 40, S(2), S(200), S(20000), 2.5, S(500), NA, NA, NA],
  [4, "CPA'dan dönüşüm sayısı", 6000, 300000, NA, NA, 3000, S(1), S(2), S(20), S(30), S(1), 200, 24000, S(4), S(800), NA, NA, NA],
  [5, "Tıklamayı CPC'den bul", 4500, 250000, NA, NA, S(3000), S(1.2), 1.5, S(18), 45, S(1.5), S(100), 13500, S(3), S(300), NA, NA, NA],
  [6, "Harcamayı CPM'den bul", S(5000), 500000, NA, NA, 5000, S(1), S(1), 10, 100, S(2), S(50), 50000, S(10), S(500), NA, NA, NA],
  [7, 'Zincir (sadece oranlar)', S(6000), S(100000), NA, NA, 2000, 2, 3, S(60), S(50), 2.5, S(120), S(20000), S(3.33), 400, NA, NA, NA],
  [8, 'Kârlı mı? (dikkat!)', 10000, 200000, NA, NA, 2000, S(1), S(5), S(50), 20, S(1), S(500), 15000, S(1.5), S(750), 40, S(2.5), S('ZARAR')],
  [9, 'Kârlı mı? (dikkat!)', 5000, 150000, NA, NA, 3000, S(2), S(1.67), S(33.33), 90, S(3), S(55.56), 30000, S(6), S(333.33), 35, S(2.86), S('KÂR')],
  [10, 'Erişim & Frekans', 9000, S(300000), 100000, 3, 6000, S(2), S(1.5), S(30), 120, S(2), S(75), 36000, S(4), S(300), NA, NA, NA],
];
const FORMULAS = [
  ['Metrik', 'Formül'],
  ['CTR (%)', 'Tıklama ÷ Gösterim × 100'],
  ['CPC (₺)', 'Harcama ÷ Tıklama'],
  ['CPM (₺)', 'Harcama ÷ Gösterim × 1000'],
  ['Dön. Oranı / CVR (%)', 'Dönüşüm ÷ Tıklama × 100'],
  ['CPA (₺)', 'Harcama ÷ Dönüşüm'],
  ['Dönüşüm Değeri (₺)', 'ROAS × Harcama  (veya AOV × Dönüşüm)'],
  ['ROAS', 'Dönüşüm Değeri ÷ Harcama'],
  ['AOV (₺)', 'Dönüşüm Değeri ÷ Dönüşüm'],
  ['Frekans', 'Gösterim ÷ Erişim'],
  ['Başa-baş ROAS', '1 ÷ Kâr Marjı'],
  ['Kâr/Zarar', 'ROAS > Başa-baş ROAS ise KÂR, değilse ZARAR'],
];

/* --- Excel stilleri: siyah grid + başlık dolgusu + binlik ayraçlı sayılar --- */
const THIN = { style: 'thin', color: { rgb: '000000' } };
const BORDER = { top: THIN, bottom: THIN, left: THIN, right: THIN };
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
  fill: { fgColor: { rgb: '111111' } },
  border: BORDER,
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
};
const BODY_STYLE = { border: BORDER, alignment: { vertical: 'center' } };
const NUM_STYLE = { border: BORDER, alignment: { horizontal: 'right', vertical: 'center' } };

// binlik ayraç: tamsayı #.### / ondalık #.###,## (Excel yerel ayarına göre . ve , gösterir)
function numFmt(v) {
  return Number.isInteger(v) ? '#,##0' : '#,##0.00';
}

function styleGrid(ws, { headerRows = 1, altFill = true } = {}) {
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const ref = XLSX.utils.encode_cell({ r: R, c: C });
      let cell = ws[ref];
      if (!cell) {
        cell = { t: 's', v: '' };
        ws[ref] = cell;
      }
      if (R < headerRows) {
        cell.s = HEADER_STYLE;
      } else if (typeof cell.v === 'number') {
        cell.s = { ...NUM_STYLE };
        cell.z = numFmt(cell.v);
      } else {
        cell.s = altFill && (R - headerRows) % 2 === 1
          ? { ...BODY_STYLE, fill: { fgColor: { rgb: 'F2F2F2' } } }
          : BODY_STYLE;
      }
    }
  }
  return ws;
}

function downloadQuiz() {
  const wb = XLSX.utils.book_new();

  const intro = [
    ['DİJİTAL PAZARLAMA — METRİK ALIŞTIRMASI'],
    [],
    ['Nasıl çözülür: "Sınav" sayfasında BOŞ hücreleri, dolu olan verileri ve formülleri kullanarak doldur.'],
    ['Bazı satırlar ters çalışır (ör. CTR + Tıklama verili → Gösterimi bul).'],
    ['8 ve 9. satırlar tuzak: ROAS yüksek görünse de kâr marjına göre ZARAR olabilir.'],
    ['Bitince "Cevap Anahtarı" sayfasından kontrol et. "Formüller" sayfası yardımcı.'],
    [],
    ['Toplam 10 senaryo · Sütunlar: gösterim, tıklama, CTR, CPC, CPM, dönüşüm, dönüşüm değeri, ROAS, AOV, kâr/zarar...'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(intro), 'Talimat');

  const colWidths = QCOLS.map((c, i) => ({ wch: i === 1 ? 24 : Math.max(9, c.length + 2) }));

  const quizAoa = [QCOLS, ...QROWS.map((r) => r.map((c) => (Array.isArray(c) ? '' : c)))];
  const wsQuiz = XLSX.utils.aoa_to_sheet(quizAoa);
  wsQuiz['!cols'] = colWidths;
  styleGrid(wsQuiz);
  XLSX.utils.book_append_sheet(wb, wsQuiz, 'Sınav');

  const ansAoa = [QCOLS, ...QROWS.map((r) => r.map((c) => (Array.isArray(c) ? c[1] : c)))];
  const wsAns = XLSX.utils.aoa_to_sheet(ansAoa);
  wsAns['!cols'] = colWidths;
  styleGrid(wsAns);
  XLSX.utils.book_append_sheet(wb, wsAns, 'Cevap Anahtarı');

  const wsF = XLSX.utils.aoa_to_sheet(FORMULAS);
  wsF['!cols'] = [{ wch: 22 }, { wch: 48 }];
  styleGrid(wsF);
  XLSX.utils.book_append_sheet(wb, wsF, 'Formüller');

  XLSX.writeFile(wb, 'glowytics-metrik-alistirmasi.xlsx');
  toast.success('Alıştırma Excel’i indirildi 📥');
}

/* ------------------------------ PAGE ------------------------------ */
function TabBtn({ active, onClick, children, accent }) {
  const cls = active
    ? accent === 'edu'
      ? 'bg-indigo-600 text-white shadow-sm'
      : 'bg-slate-900 text-white shadow-sm'
    : 'bg-white text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-700';
  return (
    <button onClick={onClick} className={`px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition ${cls}`}>
      {children}
    </button>
  );
}

function PlatformView({ p }) {
  return (
    <div>
      <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 text-white p-6 mb-7 shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300 mb-1">Platform Rehberi</p>
        <h2 className="text-xl font-bold mb-2">{p.name}</h2>
        <p className="text-[13.5px] text-slate-300 leading-relaxed">{p.intro}</p>
      </div>
      <Section title="Reklam / Kampanya türleri — hangisi ne için kurulur?">
        <AdTypeTable rows={p.types} />
        {p.note && <Note tone={p.note[0]}>{p.note[1]}</Note>}
      </Section>
      {p.targeting && (
        <Section title={p.targetingTitle || 'Hedefleme türleri & örnekler'}>
          <p>Doğru kampanya türü kadar <b>kime gösterdiğin</b> de önemli. Bu platformda kullandığımız başlıca hedefleme türleri ve birer örnek:</p>
          <InfoTable headers={['Hedefleme Türü', 'Ne demek', 'Örnek']} rows={p.targeting} />
        </Section>
      )}
      {p.examples && (
        <Section title="Örnek kampanya kurulumları">
          <p>Hedef + hedefleme + kreatifin birlikte nasıl kurulduğuna dair tipik örnekler:</p>
          <ExampleCards items={p.examples} />
        </Section>
      )}
    </div>
  );
}

export default function DigitalMarketing101() {
  const [view, setView] = useState('101');
  const isEdu = ['101', '102', '103'].includes(view);

  const activeLabel =
    [...EDU_TABS, ...PLATFORM_TABS].find((t) => t.id === view)?.label || '';

  return (
    <div className="bg-slate-100 min-h-screen py-6 px-3 sm:px-6">
      <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-md ring-1 ring-slate-200 overflow-hidden">
        {/* Cover */}
        <header className="relative bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-600 text-white px-7 sm:px-10 py-9">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          <div className="relative">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-white/15 backdrop-blur px-2.5 py-1 rounded-full mb-3">
              📘 Glowytics Akademi
            </p>
            <h1 className="text-[28px] sm:text-[32px] font-extrabold tracking-tight leading-tight">Dijital Pazarlama El Kitabı</h1>
            <p className="text-[14px] text-indigo-100 mt-2 max-w-xl leading-relaxed">
              Sıfırdan ileri seviyeye. Metrikler, genel mantık, e-ticaret takibi ve her platformun reklam türleri — başlık başlık.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-[12px] text-indigo-100">
              <span>🎯 Seviye: 101 → 103</span>
              <span>⏱️ ~25 dk okuma</span>
              <span>📊 5 platform rehberi</span>
            </div>
          </div>
        </header>

        {/* Sticky chapter nav */}
        <nav className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200 px-4 sm:px-6 py-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-[78px]">Eğitim</span>
              {EDU_TABS.map((t) => (
                <TabBtn key={t.id} accent="edu" active={view === t.id} onClick={() => setView(t.id)}>{t.label}</TabBtn>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-[78px]">Platformlar</span>
              {PLATFORM_TABS.map((t) => (
                <TabBtn key={t.id} accent="plat" active={view === t.id} onClick={() => setView(t.id)}>{t.label}</TabBtn>
              ))}
            </div>
          </div>
        </nav>

        <div className="px-5 sm:px-10 py-8">
          {/* Chapter heading */}
          <div className="flex items-center gap-2 mb-6">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{activeLabel}</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          {/* Big picture — only on edu tabs */}
          {isEdu && (
            <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-5 sm:p-6 mb-8">
              <h2 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><span>🗺️</span> Büyük Resim — Bu iş aslında ne?</h2>
              <p className="text-[14px] text-slate-600 leading-7">
                Dijital pazarlama; bir markanın ürün/hizmetini internet üzerinden <b>doğru kişiye, doğru zamanda, doğru mesajla</b> ulaştırıp
                para kazandırma işidir. Geleneksel reklamdan farkı: <b>her şey ölçülebilir.</b> Bizim (Glowytics) işimiz bu veriyi toplamak,
                anlamlandırmak ve “bütçeyi nereye koyarsak daha çok kazanırız?” sorusuna cevap vermek.
              </p>
              <Code>{`   FARKINDALIK   →  markayı ilk kez görür           (çok kişi)
   İLGİ          →  merak eder, siteye gelir
   DEĞERLENDİRME →  ürünü inceler, sepete atar
   DÖNÜŞÜM       →  satın alır                       (az kişi)
   SADAKAT       →  tekrar alır, tavsiye eder`}</Code>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Yukarıdan aşağı insan sayısı azalır (huni / funnel). İşimizin büyük kısmı insanları bir alt adıma geçirmek ve her adımdaki kaybı azaltmak.
              </p>
            </div>
          )}

      {/* ============================ 101 ============================ */}
      {view === '101' && (
        <div className="space-y-8">
          <Section n="1.1" title="Kanallar — Reklamı nereye veriyoruz?">
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ['Google Ads (Arama)', 'İnsan zaten “X ürünü” diye aratıyor, biz çıkıyoruz. → Talebi yakalama.'],
                ['Google (PMax / Shopping / Display)', 'Google’ın tüm ağında otomatik reklam. → Tüm huni.'],
                ['Meta Ads (Facebook + Instagram)', 'İnsan aramıyor, biz önüne çıkıyoruz. → Talep yaratma.'],
                ['TikTok Ads', 'Genç kitle, video, eğlence. → Farkındalık.'],
                ['Pinterest / Criteo / RTB House', 'Görsel keşif & yeniden hedefleme (retargeting).'],
                ['E-posta / SMS', 'Mevcut müşteriye doğrudan ulaşma. → Sadakat.'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 bg-white shadow-sm p-3.5 hover:border-indigo-200 hover:shadow transition">
                  <p className="text-[13px] font-bold text-slate-800">{k}</p>
                  <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{v}</p>
                </div>
              ))}
            </div>
            <Note tone="blue">
              <b>Kritik ayrım:</b> <b>Google = talebi yakalama</b> (insan zaten istiyor). <b>Meta/TikTok = talep yaratma</b>
              {' '}(insan istediğini bilmiyordu). İkisi farklı mantıkla çalışır; aynı metrikle yargılamak yanlış olur. Detay için yukarıdaki platform sekmeleri.
            </Note>
          </Section>

          <Section n="1.2" title="Temel metrikler — ezberlenecek sözlük">
            <p>İşin alfabesi. Önce erişim/maliyet, sonra dönüşüm/gelir. Her birini formülüyle öğren.</p>
            <MetricTable title="① Erişim & Tıklama" rows={METRICS_REACH} />
            <MetricTable title="② Maliyet metrikleri" rows={METRICS_COST} />
            <MetricTable title="③ Dönüşüm & Gelir" rows={METRICS_CONV} />

            <div className="rounded-2xl border-2 border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 p-5 my-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex gap-3">
                  <span className="text-2xl">📥</span>
                  <div>
                    <p className="text-sm font-bold text-slate-800">Metrik Alıştırması (Excel)</p>
                    <p className="text-[13px] text-slate-600 mt-1 max-w-md leading-relaxed">
                      10 “gıcık” senaryo. Boş bırakılan metrikleri (ör. elinde CTR + tıklama var, gösterimi bul) hesaplayıp doldur.
                      Conversion, conversion value, ROAS, kâr/zarar dahil. İçinde cevap anahtarı + formül sayfası var.
                    </p>
                  </div>
                </div>
                <button
                  onClick={downloadQuiz}
                  className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-sm whitespace-nowrap transition"
                >
                  Excel’i indir
                </button>
              </div>
            </div>

            <p className="font-semibold text-slate-700 mt-3">En çok kafa karıştıran üçlü:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>CTR düşük</b> → reklam (görsel/metin) ilgi çekmiyor. Sorun <b>reklamda.</b></li>
              <li><b>CTR yüksek ama CVR düşük</b> → reklam iyi ama site/ürün/fiyat ikna etmiyor. Sorun <b>landing page’de.</b></li>
              <li><b>ROAS = 4</b> → 1 TL harcadık, 4 TL ciro geldi. Ama kâr için marja bak (102 → Break-even ROAS).</li>
            </ul>
          </Section>

          <Section n="1.3" title="Bir reklam hesabının yapısı (hiyerarşi)">
            <Code>{`HESAP (Account)
 └── KAMPANYA          ← amaç + bütçe (satış? trafik?)
      └── REKLAM SETİ / Ad Group   ← hedef kitle + nerede
           └── REKLAM   ← görsel + metin + video (kullanıcının gördüğü)`}</Code>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Kampanya</b> = “Ne istiyorum?” (ör. satış)</li>
              <li><b>Reklam Seti / Ad Group</b> = “Kime, nerede?” (ör. 25-45 yaş, İstanbul)</li>
              <li><b>Reklam</b> = “Ne göstereceğim?” (görsel + mesaj)</li>
            </ul>
          </Section>

          <Section n="1.4" title="Pixel & takip — veriyi nasıl topluyoruz?">
            <p>
              Reklamın işe yarayıp yaramadığını bilmek için sitedeki olayları yakalamamız lazım. Bunu <b>pixel</b> (Meta Pixel, Google Tag)
              denen küçük kod parçaları yapar. Takip ettiğimiz tipik olaylar:
            </p>
            <Code>{`Sayfa görüntüleme → Ürün görüntüleme → Sepete ekleme → Ödeme başlatma → Satın alma`}</Code>
            <Note tone="rose">
              Pixel olmadan dijital pazarlama <b>kör uçuştur.</b> Bir markayla çalışmaya başlarken ilk işimiz takibin doğru kurulduğunu kontrol etmektir.
            </Note>
          </Section>

          <Section n="1.5" title="E-ticaret & Google Analytics (GA4) — neyi neden takip ederiz?">
            <p>
              Reklam platformları “bana tıklayan ne yaptı?” der; <b>GA4</b> ise sitenin <b>bağımsız</b> gözüdür: kullanıcının huni içinde nerede
              düştüğünü gösterir. E-ticarette her adımı ayrı bir <b>olay (event)</b> olarak takip ederiz.
            </p>
            <MetricTable title="GA4 & e-ticaret olayları / metrikleri" rows={METRICS_GA4} />
            <Note tone="emerald">
              <b>Neden kritik?</b> <code>view_item</code> (ürün detay görüntüleme) yüksek ama <code>add_to_cart</code> düşükse → fiyat/görsel/stok sorunu.
              <code> begin_checkout</code> yüksek ama <code>purchase</code> düşükse → kargo ücreti / ödeme adımı sorunu. Reklamı suçlamadan önce hunideki
              <b> kopma noktasını</b> GA4’ten buluruz.
            </Note>
          </Section>
        </div>
      )}

      {/* ============================ 102 ============================ */}
      {view === '102' && (
        <div className="space-y-8">
          <Section n="2.1" title="Attribution (atıf) — bu satışı kim getirdi?">
            <p>
              Bir müşteri almadan önce <b>birden çok reklamla</b> temas eder: önce Instagram’da gördü, sonra Google’da aratıp tıkladı, sonra geri gelip aldı.
              <b> Attribution</b> = satışın hangi temasa/kanala atfedileceğinin kuralı. Modeller:
            </p>
            <InfoTable headers={['Model', 'Mantık', 'Not / kullanım']} rows={ATTRIBUTION} />
            <Note tone="emerald">
              <b>Biz Data-Driven (DDA) kullanırız.</b> Tek bir tıklamaya tüm krediyi vermek yerine, algoritma gerçek dönüşüm yollarına bakıp her temasın
              katkısını dağıtır. Last-click üst hunideki (Meta/YouTube) katkıyı görmezden geldiği için yanıltıcıdır.
            </Note>
            <Note tone="rose">
              <b>Çift sayım tuzağı:</b> Her platform satışı <b>kendine</b> yazar. Meta “benim” der, Google “benim” der → toplarsan gerçekten fazla çıkar.
              Gerçek resmi görmek için bağımsız kaynağa (GA4 / sitenin gerçek sipariş verisi) bakarız, kanalları körlemesine toplamayız.
            </Note>
          </Section>

          <Section n="2.2" title="Hedef kitle (targeting) türleri">
            <ol className="list-decimal pl-5 space-y-1">
              <li><b>İlgi/Demografi</b> — yaş, cinsiyet, ilgi alanı.</li>
              <li><b>Custom Audience</b> — bizim verimiz: site ziyaretçileri, müşteri listesi.</li>
              <li><b>Retargeting</b> — sepete atıp almayanlara tekrar reklam. <b>En yüksek ROAS buradadır</b> (kişi zaten ilgili).</li>
              <li><b>Lookalike / Benzer Kitle</b> — “en iyi müşterilerime benzeyenleri bul”.</li>
            </ol>
            <Note tone="blue">
              <b>Strateji:</b> Soğuk kitle (hiç tanımayan) ile sıcak kitleyi (sepete atan) aynı reklamla vuramazsın. Soğuğa “tanıştırma”, sıcağa “indirim/aciliyet” gider.
            </Note>
          </Section>

          <Section n="2.3" title="Bütçe & teklif (bidding) mantığı">
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Günlük vs Toplam (lifetime) bütçe</b> — günde ne kadar / kampanya boyunca ne kadar.</li>
              <li><b>Maximize Conversions</b> — “bütçeyi harca, en çok dönüşümü getir”.</li>
              <li><b>Target ROAS (tROAS)</b> — “şu ROAS’ı tuttur”.</li>
              <li><b>Target CPA</b> — “müşteriyi şu maliyete getir”.</li>
            </ul>
            <Note>
              <b>Öğrenme aşaması (learning phase):</b> Yeni kampanya ~3-7 gün / ~50 dönüşüm öğrenir. <b>Bu süreçte sürekli müdahale etme</b>, yoksa öğrenmeyi sıfırlarsın. (En sık acemi hatası.)
            </Note>
          </Section>

          <Section n="2.4" title="Birim ekonomisi — kâr mı ediyoruz?">
            <p>Ecem’i diğerlerinden ayıracak kısım burası. ROAS’a takılıp kâra bakmamak en büyük hata.</p>
            <Code>{`Break-even ROAS = 1 ÷ Kâr Marjı

Örnek: marj %40 ise → 1 ÷ 0,40 = 2,5
→ ROAS 2,5 ALTI = zarar,  ÜSTÜ = kâr.`}</Code>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>CAC</b> — yeni müşteri kazanma maliyeti.</li>
              <li><b>LTV</b> — bir müşterinin ömrü boyunca getirdiği toplam ciro.</li>
              <li><b>Altın kural: LTV &gt; CAC.</b> İlk satışta zarar etsen bile müşteri tekrar alıyorsa reklam kârlıdır. Bu yüzden bazı markada “düşük ROAS”a rağmen devam ederiz.</li>
            </ul>
            <Note tone="blue">Excel alıştırmasındaki 8 ve 9. satırlar tam olarak bunu test ediyor: aynı ROAS farklı marjda kâr ya da zarar olabilir.</Note>
          </Section>

          <Section n="2.5" title="A/B testi — tahmin etme, test et">
            <ul className="list-disc pl-5 space-y-1">
              <li>Aynı anda 2 varyant (A ve B) — sadece <b>tek bir şeyi</b> değiştir (görsel VEYA başlık, ikisini birden değil).</li>
              <li>Yeterli veri birikene kadar bekle (istatistiksel anlamlılık) — 5 tıklamayla karar verilmez.</li>
              <li>Kazananı tut, kaybedeni kapat, yeni test başlat. Döngü hiç bitmez.</li>
            </ul>
          </Section>

          <Section n="2.6" title="Funnel metrikleri — nerede kan kaybediyoruz?">
            <Code>{`Gösterim → Tıklama          :  CTR
Tıklama → Ürün görüntüleme  :  landing kalitesi
Ürün → Sepete ekleme        :  add-to-cart rate
Sepet → Ödeme başlatma      :
Ödeme → Satın alma          :  checkout completion`}</Code>
            <p>Örn. sepete çok atılıyor ama satın alma düşükse → sorun kargo ücreti / ödeme adımı / fiyattadır, reklamda değil.</p>
          </Section>
        </div>
      )}

      {/* ============================ 103 ============================ */}
      {view === '103' && (
        <div className="space-y-8">
          <Section n="3.1" title="Biz (Glowytics) tam olarak ne yapıyoruz?">
            <p>
              Biz bir <b>dijital pazarlama analitik platformuyuz.</b> Markaların tüm reklam kanallarındaki (Google, Meta, TikTok, Pinterest, Criteo, RTB, Adjust…)
              verisini <b>tek yerde</b> toplayıp anlamlandırıyoruz. Müşteri 5 panele bakmak yerine bizde tek ekranda her şeyi görüyor + biz yorumluyor + aksiyon öneriyoruz.
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li><b>Veri toplama</b> — her kanaldan API ile otomatik (cron’larla günde birkaç kez).</li>
              <li><b>Birleştirme</b> — hepsini ortak formata getiriyoruz (unified view).</li>
              <li><b>Analiz & raporlama</b> — markaya özel dashboard, haftalık/aylık raporlar.</li>
              <li><b>İçgörü & aksiyon</b> — “şu kampanya zarar ediyor, kapat”, “şu ürüne bütçe kaydır”.</li>
            </ol>
          </Section>

          <Section n="3.2" title="Multi-tenancy (çok marka mantığı)">
            <p>
              Platformda <b>150+ marka</b> var ve her birinin verisi birbirinden <b>tamamen izole.</b> Her markanın <code>brandId</code>’si var, verisi ayrı tutuluyor.
              Bir markayla çalışırken hep “doğru marka seçili mi?” diye kontrol et.
            </p>
          </Section>

          <Section n="3.3" title="Pacing (bütçe temposu) — ay sonunu vuracak mıyız?">
            <Code>{`Beklenen harcama = Aylık bütçe × (geçen gün ÷ aydaki gün)

Gerçek > Beklenen → hızlı harcıyoruz (yavaşlat)
Gerçek < Beklenen → geri kaldık (hızlandır)`}</Code>
            <p>Her gün takip etmek, ay sonunda “bütçe yanmış / hedef kaçmış” sürprizini engeller.</p>
          </Section>

          <Section n="3.4" title="Raporlama — iyi rapor nasıl olur?">
            <p>Müşteriye giden rapor <b>veri yığını değil, hikâye</b> olmalı:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Ne oldu?</b> (geçen döneme göre kıyas — çıplak sayı değil, <b>değişim</b>)</li>
              <li><b>Neden oldu?</b> (yorum: “ROAS düştü çünkü yeni kampanya öğrenme aşamasında”)</li>
              <li><b>Ne yapacağız?</b> (aksiyon önerisi)</li>
              <li>Her zaman kıyaslama koy: dönem-dönem (WoW/MoM), hedefe karşı, geçen yıla karşı (YoY).</li>
            </ul>
          </Section>

          <Section n="3.5" title="Sezgisel kontrol — bu veri yalan söylüyor">
            <ul className="list-disc pl-5 space-y-1">
              <li><b>ROAS aniden 50 oldu</b> → muhtemelen çift sayım (attribution) ya da pixel iki kez ateşliyor.</li>
              <li><b>Harcama var, dönüşüm sıfır</b> → pixel bozulmuş olabilir, takip kontrol et.</li>
              <li><b>Bir günde veri yok</b> → veri çekimi (sync) patlamış olabilir.</li>
              <li>Platform “satış benim” diyor ama sitenin gerçek siparişi farklı → bağımsız kaynağa güven.</li>
            </ul>
            <Note tone="rose">
              Kural: <b>Bir sayı çok iyi ya da çok kötü görünüyorsa, aksiyona geçmeden önce doğrula.</b> Çoğu zaman ölçüm hatasıdır.
            </Note>
          </Section>

          <Section n="3.6" title="Sık yapılan hatalar (ezberle, yapma)">
            <ol className="list-decimal pl-5 space-y-1">
              <li>Öğrenme aşamasındaki kampanyaya sürekli müdahale etmek.</li>
              <li>ROAS’a bakıp kâr marjını unutmak.</li>
              <li>Birkaç günlük az veriyle büyük karar vermek.</li>
              <li>Tüm kanalların satışını toplayıp “toplam satış bu” demek (çift sayım).</li>
              <li>Soğuk ve sıcak kitleye aynı mesajı vermek.</li>
              <li>Reklamı suçlamak — oysa çoğu zaman sorun landing page / fiyat / stok.</li>
              <li>Retargeting ROAS’ına bakıp “harika kanal” sanmak (o kitle zaten alacaktı).</li>
            </ol>
          </Section>
        </div>
      )}

      {/* ============================ Platform tabs ============================ */}
      {!isEdu && <PlatformView p={PLATFORMS[view]} />}

          {/* Quiz footer — only edu tabs */}
          {isEdu && (
            <div className="mt-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-sm p-5 sm:p-6">
              <h2 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><span>🧠</span> Hızlı Sınav (kendini test et)</h2>
              <ol className="list-decimal pl-5 space-y-1.5 text-[14px] text-slate-600 leading-7">
                <li>CTR yüksek ama satış yok — sorun nerede?</li>
                <li>Marj %25 olan üründe break-even ROAS kaç?</li>
                <li>Meta ve Google ikisi de “bu satış benim” diyor — gerçek satışı nasıl bulurum?</li>
                <li>Yeni kampanya 2 gündür kötü gidiyor, kapatayım mı?</li>
                <li>view_item yüksek, add_to_cart düşük — nereye bakarım?</li>
              </ol>
              <details className="mt-4 group">
                <summary className="cursor-pointer text-[13px] font-semibold text-indigo-600 hover:text-indigo-700 select-none">▸ Cevapları göster</summary>
                <p className="text-[13px] text-slate-500 mt-2 leading-7 bg-white rounded-lg border border-slate-100 p-3">
                  1- Landing page / fiyat. &nbsp; 2- 4 (1 ÷ 0,25). &nbsp; 3- Bağımsız kaynak/GA4’e bak, kanalları toplama (data-driven). &nbsp;
                  4- Hayır, öğrenme aşaması — bekle. &nbsp; 5- Ürün fiyatı/görseli/stok ve PDP deneyimi (detayı gören sepete atmıyor).
                </p>
              </details>
            </div>
          )}

          <div className="mt-10 pt-5 border-t border-slate-200 text-center">
            <p className="text-[11px] text-slate-400 font-medium">Glowytics Akademi · Dijital Pazarlama El Kitabı</p>
            <p className="text-[10px] text-slate-300 mt-0.5">101 · 102 · 103 + Platform Rehberleri</p>
          </div>
        </div>
      </article>
    </div>
  );
}
